/**
 * Sprint 5.C — Backend-sourced unified inbox.
 */
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAppStore } from "@/store/useAppStore";
import { useAssistantName } from "@/store/useTeamStore";
import ReplyComposer from "@/components/inbox/ReplyComposer";
import { buildQuotedReplyHtml, resolveReplyComposerContent } from "@/lib/emailComposer";
import { usePersonaStore } from "@/store/usePersonaStore";
import { InboxChannelTabs } from "@/components/inbox/InboxChannelTabs";
import { InboxToolbar } from "@/components/inbox/InboxToolbar";
import { channelRequiresDraftApproval, hiddenThreadStatuses } from "@/lib/inboxChannels";
import { useInboxData } from "@/pages/inbound/useInboxData";
import { useInboxActions } from "@/pages/inbound/useInboxActions";
import {
  ConversationListItem,
  EmptyInbox,
  EmptyInboxFiltered,
  InboxDetailPanel,
} from "@/components/inbox/backend/BackendInboxComponents";

export default function BackendInbox() {
  const { auth } = useAppStore();
  const role = auth?.server?.role || "member";
  const canApprove = role === "owner" || role === "admin";
  const canUpdateStatus = role !== "viewer";
  const assistantName = useAssistantName();
  const emailSignature = usePersonaStore((s) => s.signatures?.email || "");

  const [busyAction, setBusyAction] = useState(null);
  const [retryId, setRetryId] = useState(null);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerMode, setComposerMode] = useState("edit");
  const [composerMessageId, setComposerMessageId] = useState(null);
  const [composerSession, setComposerSession] = useState(0);

  const inbox = useInboxData();
  const {
    conversationId,
    activeChannel,
    activeFilter,
    hasActiveFilters,
    globalEmpty,
    search,
    setSearch,
    list,
    setList,
    listTotal,
    inboxSummary,
    listLoading,
    syncing,
    activeConv,
    setActiveConv,
    activeMessages,
    setActiveMessages,
    detailLoading,
    draftGenerating,
    setDraftGenerating,
    threadScrollerRef,
    setActiveChannel,
    setActiveFilter,
    isSelectedConversation,
    selectConversation,
    clearConversation,
    refreshList,
    loadDetail,
    maintainThreadScroll,
  } = inbox;

  const showMobileDetail = !!conversationId;
  const requiresDraftApproval = channelRequiresDraftApproval(activeConv?.channel);
  const HIDDEN_THREAD_STATUSES = useMemo(
    () => hiddenThreadStatuses(activeConv?.channel),
    [activeConv?.channel],
  );
  const composerMessage = useMemo(() => {
    if (!composerMessageId) return null;
    return activeMessages.find((m) => m.id === composerMessageId) || null;
  }, [activeMessages, composerMessageId]);
  const lastInbound = useMemo(
    () => activeMessages.find((m) => m.direction === "inbound" && m.status === "received"),
    [activeMessages],
  );
  const drafts = useMemo(() => {
    const pending = activeMessages.filter((m) => m.status === "draft");
    // Oldest first so Reply 1 matches the earliest unanswered inbound.
    return [...pending].sort((a, b) => {
      const ta = new Date(a.created_at || 0).getTime();
      const tb = new Date(b.created_at || 0).getTime();
      return ta - tb;
    });
  }, [activeMessages]);
  const [selectedDraftId, setSelectedDraftId] = useState("");
  useEffect(() => {
    if (!drafts.length) {
      setSelectedDraftId("");
      return;
    }
    if (!drafts.some((d) => String(d.id) === String(selectedDraftId))) {
      // Default to Reply 1 (oldest pending draft).
      setSelectedDraftId(drafts[0].id);
    }
  }, [drafts, selectedDraftId]);
  const draft = useMemo(
    () => drafts.find((m) => String(m.id) === String(selectedDraftId)) || drafts[drafts.length - 1] || null,
    [drafts, selectedDraftId],
  );
  const channel = (activeConv?.channel || "").toLowerCase();
  const isEmailChannel = ["email", "gmail"].includes(channel);

  const {
    onStatusChange,
    onApprove,
    onRetryOutbound,
    onDiscard,
    onRegenerateDraft,
    onSendManual,
    onComposerSave,
    onComposerSend,
  } = useInboxActions({
    activeConv,
    activeFilter,
    draft,
    composerMessageId,
    composerMessage,
    composerMode,
    isEmailChannel,
    assistantName,
    setBusyAction,
    setRetryId,
    setComposerOpen,
    setComposerMode,
    setComposerMessageId,
    setActiveConv,
    setList,
    setActiveMessages,
    setDraftGenerating,
    loadDetail,
    refreshList,
  });

  const visibleMessages = useMemo(
    () => activeMessages.filter((m) => !HIDDEN_THREAD_STATUSES.has(m.status)),
    [activeMessages, HIDDEN_THREAD_STATUSES],
  );

  useEffect(() => {
    maintainThreadScroll(visibleMessages);
  }, [visibleMessages, maintainThreadScroll]);

  useEffect(() => {
    setComposerOpen(false);
    setComposerMessageId(null);
    setBusyAction(null);
    setRetryId(null);
  }, [activeConv?.id]);

  const openComposerEdit = () => {
    if (!draft) return;
    setComposerMode("edit");
    setComposerMessageId(draft.id);
    setComposerSession((n) => n + 1);
    setComposerOpen(true);
  };

  const openAcceptRequest = () => {
    // Meta accepts Instagram message requests when the first reply is sent.
    // Open the draft editor when available; otherwise open a blank reply composer.
    if (draft) {
      openComposerEdit();
      return;
    }
    setComposerMode("reply");
    setComposerMessageId(null);
    setComposerSession((n) => n + 1);
    setComposerOpen(true);
  };

  const quotedReplyHtml = useMemo(() => {
    if (!lastInbound) return "";
    return buildQuotedReplyHtml(lastInbound.body_text, activeConv?.contact_name);
  }, [lastInbound, activeConv?.contact_name]);

  const composerInitial = useMemo(() => {
    const msg = composerMessage || draft;
    return resolveReplyComposerContent(msg);
  }, [composerMessage, draft]);

  if (listLoading && !inboxSummary) {
    return (
      <div className="flex h-full items-center justify-center bg-white" data-testid="inbox-loading">
        <Loader2 size={20} className="animate-spin text-indigo-600" />
      </div>
    );
  }

  if (globalEmpty) {
    return <EmptyInbox />;
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 overflow-hidden bg-white flex-col" data-testid="backend-inbox">
      <div className={cn(showMobileDetail && "hidden lg:block")}>
        <InboxChannelTabs
          activeChannel={activeChannel}
          onChange={setActiveChannel}
          inboxSummary={inboxSummary}
        />
      </div>
      <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
        <aside
          className={cn(
            "shrink-0 border-r border-slate-200 flex-col bg-white min-h-0 min-w-0",
            showMobileDetail
              ? "hidden lg:flex lg:w-[340px]"
              : "flex w-full lg:w-[340px]",
          )}
          data-testid="inbox-list-pane"
        >
          <div className="h-12 shrink-0 px-3 border-b border-slate-200 flex items-center justify-between">
            <div className="text-[12.5px] font-semibold text-slate-900">Conversations</div>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 sm:h-7 sm:w-7"
              onClick={() => refreshList({ sync: true })}
              title="Refresh & sync"
              disabled={syncing || listLoading}
              data-testid="inbox-refresh-btn"
            >
              <RefreshCw size={13} className={cn("text-slate-500", (syncing || listLoading) && "animate-spin")} />
            </Button>
          </div>
          <InboxToolbar
            search={search}
            onSearchChange={setSearch}
            activeFilter={activeFilter}
            onFilterChange={setActiveFilter}
            resultCount={listTotal}
          />
          {list.length === 0 ? (
            <EmptyInboxFiltered
              channel={activeChannel}
              inboxSummary={inboxSummary}
              hasFilters={hasActiveFilters}
            />
          ) : (
            <ul className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-slate-50 min-h-0" data-testid="inbox-list">
              {list.map((c) => (
                <ConversationListItem
                  key={c.id}
                  conv={c}
                  active={isSelectedConversation(c.id)}
                  onClick={() => selectConversation(c.id)}
                />
              ))}
            </ul>
          )}
        </aside>

        <div
          className={cn(
            "min-w-0 min-h-0 flex-col bg-slate-50/40",
            showMobileDetail
              ? "flex flex-1"
              : "hidden lg:flex lg:flex-1",
          )}
          data-testid="inbox-detail"
        >
          <InboxDetailPanel
            detailLoading={detailLoading}
            activeConv={activeConv}
            activeChannel={activeChannel}
            inboxSummary={inboxSummary}
            hasActiveFilters={hasActiveFilters}
            listLength={list.length}
            threadScrollerRef={threadScrollerRef}
            visibleMessages={visibleMessages}
            assistantName={assistantName}
            canApprove={canApprove}
            canUpdateStatus={canUpdateStatus}
            busyAction={busyAction}
            retryId={retryId}
            draft={draft}
            drafts={drafts}
            selectedDraftId={selectedDraftId}
            onSelectDraft={setSelectedDraftId}
            draftGenerating={draftGenerating}
            requiresDraftApproval={requiresDraftApproval}
            hideFooter={composerOpen}
            onBack={clearConversation}
            onStatusChange={onStatusChange}
            onRetryOutbound={onRetryOutbound}
            onApprove={onApprove}
            onDiscard={onDiscard}
            onRegenerateDraft={onRegenerateDraft}
            onSendManual={onSendManual}
            openComposerEdit={openComposerEdit}
            onAcceptRequest={openAcceptRequest}
          />
          <ReplyComposer
            open={composerOpen}
            onOpenChange={setComposerOpen}
            mode={composerMode}
            draftKey={composerMessageId || `${composerMode}-${activeConv?.id || ""}`}
            forceExpandKey={`edit-session-${composerSession}`}
            conversationId={activeConv?.id || ""}
            initialBodyText={composerInitial.bodyText}
            initialBodyHtml={composerInitial.bodyHtml}
            signature={emailSignature}
            quotedHtml={composerMode === "reply" ? quotedReplyHtml : ""}
            assistantName={assistantName}
            busy={busyAction === "edit" || busyAction === "send"}
            onSave={onComposerSave}
            onSend={onComposerSend}
          />
        </div>
      </div>
    </div>
  );
}
